<template>
  <div class="invoices-pending-wrapper">
    <b-card no-body>
      <div class="m-2">
        <b-row>
          <b-col md="12">
            <h2>{{ $t("cashiers.pending-invoices-title") }}</h2>
          </b-col>
          <b-col
            cols="12"
            md="12"
          >
            <br></b-col>

          <b-col
            md="3"
            cols="12"
          >
            <b-form-group
              :label="$t('common.date')"
              label-for="filter-date"
            >
              <datepicker
                id="filter-date"
                v-model="filterDate"
                :calendar-button="true"
                :clear-button="true"
                :bootstrap-styling="true"
                class="datebtn2"
                placeholder="YYYY-MM-DD"
              />
            </b-form-group>
          </b-col>
          <b-col
            md="3"
            cols="12"
          >
            <b-form-group
              :label="$t('common.type')"
              label-for="type-filter"
            >
              <v-select
                id="type-filter"
                v-model="filterType"
                :options="invoiceTypeOptions"
                label="text"
                :reduce="(option) => option.value"
                :clearable="true"
                :placeholder="$t('common.type')"
              />
            </b-form-group>
          </b-col>
          <b-col
            md="3"
            cols="12"
          >
            <b-form-group
              :label="$t('reception.patient-name')"
              label-for="patient-filter"
            >
              <b-form-input
                id="patient-filter"
                v-model="patientName"
                :placeholder="$t('reception.patient-name')"
                @keyup.enter="fetchPendingInvoices()"
              />
            </b-form-group>
          </b-col>
          <b-col
            md="3"
            cols="12"
          >
            <b-form-group
              :label="$t('common.invoice-number')"
              label-for="invoice-number-filter"
            >
              <b-form-input
                id="invoice-number-filter"
                v-model="filterInvoiceNumber"
                :placeholder="$t('common.invoice-number')"
                @keyup.enter="fetchPendingInvoices()"
              />
            </b-form-group>
          </b-col>
        </b-row>

        <b-row class="mt-1">
          <b-col
            md="3"
            cols="12"
          >
            <b-form-group
              label="نوع الخدمة"
              label-for="service-type-filter"
            >
              <v-select
                id="service-type-filter"
                v-model="filterServiceType"
                :options="serviceTypeFilterOptions"
                label="text"
                :reduce="(option) => option.value"
                :clearable="true"
                placeholder="نوع الخدمة"
              />
            </b-form-group>
          </b-col>
        </b-row>

        <b-row>
          <b-col
            cols="12"
            md="6"
            class="d-flex align-items-center justify-content-end mb-1 mb-md-0"
          >
            <b-button
              class="mt-50 mr-2"
              variant="primary"
              @click="printCurrentInvoices"
            >
              طباعة كشف يومي
              <feather-icon
                icon="PrinterIcon"
                class="ml-1"
                size="16"
              />
            </b-button>
            <b-button
              class="mt-50"
              variant="success"
              @click="showBulkConfirmModal = true"
            >
              تأكيد التسليم
              <feather-icon
                icon="CheckIcon"
                class="ml-1"
                size="16"
              />
            </b-button>
          </b-col>
        </b-row>

        <b-row class="mt-3">
          <b-col>
            <b-table
              v-if="!loading"
              ref="pendingTable"
              :items="preparedInvoices"
              responsive
              :fields="tableColumns"
              primary-key="invoiceId"
              class="position-relative"
              show-empty
              :empty-text="$t('common.no-invoices-found')"
            >
              <template #cell(invoiceId)="data">
                <span class="font-weight-bold">{{ data.item.invoiceId }}</span>
              </template>
              <template #cell(type)="data">
                <span class="text-nowrap">{{
                                            formatInvoiceType(data.item.type)
                                          }}
                  <span v-if="data.item.serviceTypeName">
                    ({{ data.item.serviceTypeName }})
                  </span>
                </span>
              </template>
              <template #cell(number)="data">
                <span class="text-nowrap">{{ data.item.number }}</span>
              </template>
              <template #cell(patientName)="data">
                <span>{{ data.item.patientName }}</span>
              </template>
              <template #cell(itemTitle)="data">
                <span>{{ data.item.itemTitle }}</span>
              </template>
              <template #cell(serviceTypeName)="data">
                <span>{{ data.item.serviceTypeName }}</span>
              </template>
              <template #cell(amount)="data">
                <span class="font-weight-bold">{{
                  formatAmount(data.item.amount, data.item.currency)
                }}</span>
              </template>
              <template #cell(discount)="data">
                <span>{{
                  formatAmount(data.item.discount || 0, data.item.currency)
                }}</span>
              </template>
              <template #cell(status)="data">
                <b-badge variant="warning">
                  {{ data.item.status }}
                </b-badge>
              </template>
              <template #cell(approvedBy)="data">
                <div v-if="data.item.approvedBy">
                  <div class="font-weight-bold">
                    {{ data.item.approvedBy.name || "-" }}
                  </div>
                  <div class="text-muted small">
                    {{ data.item.approvedBy.email || "" }}
                  </div>
                </div>
                <span v-else>-</span>
              </template>
              <template #cell(approvedDate)="data">
                <span class="text-nowrap">{{
                  formatDate(data.item.approvedDate)
                }}</span>
              </template>
              <template #cell(createdBy)="data">
                <span>{{ data.item.createdBy || "-" }}</span>
              </template>
              <template #cell(createdAt)="data">
                <span class="text-nowrap">{{
                  formatDate(data.item.createdAt)
                }}</span>
              </template>
              <template #cell(actions)="data">
                <div class="text-nowrap">
                  <b-button
                    size="sm"
                    pill
                    variant="success"
                    class="mb-75"
                    @click="openReceiveModal(data.item.invoiceId)"
                  >
                    {{ $t("cashiers.receive-payment") }}
                  </b-button>
                </div>
              </template>
            </b-table>

            <template v-if="loading">
              <div>
                <div class="d-flex justify-content-center mb-1">
                  <b-spinner label="Loading..." />
                </div>
              </div>
            </template>
          </b-col>
        </b-row>
      </div>

      <div
        v-if="dataCount > 0"
        class="mx-2 mb-2"
      >
        <b-row>
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-start"
          >
            <span class="text-muted mr-2">
              {{ $t("common.showing") }} {{ (page - 1) * take + 1 }}
              {{ $t("common.to") }} {{ Math.min(page * take, dataCount) }}
              {{ $t("common.of") || "of" }} {{ dataCount }}
            </span>
            <b-form-select
              v-model="take"
              :options="pageSizeOptions"
              size="sm"
              style="width: auto; min-width: 80px"
              class="ml-2"
              @change="onPageSizeChange"
            />
          </b-col>
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-end"
          >
            <b-pagination
              v-model="page"
              :total-rows="dataCount"
              :per-page="take"
              first-number
              last-number
              class="mb-0 mt-1 mt-sm-0"
              prev-class="prev-item"
              next-class="next-item"
            >
              <template #prev-text>
                <feather-icon
                  icon="ChevronLeftIcon"
                  size="18"
                />
              </template>
              <template #next-text>
                <feather-icon
                  icon="ChevronRightIcon"
                  size="18"
                />
              </template>
            </b-pagination>
          </b-col>
        </b-row>
      </div>

      <b-modal
        id="receive-payment-modal"
        :visible.sync="showReceiveModal"
        hide-footer
        @hide="selectedInvoiceId = null"
      >
        <template #modal-title>
          {{ $t("cashiers.receive-payment-title") }}
        </template>
        <div class="my-2">
          <p class="mb-2">
            {{
              $t("cashiers.confirm-receive-prompt") ||
                "Are you sure you want to mark this invoice as received?"
            }}
          </p>
          <div class="d-flex justify-content-end">
            <b-button
              variant="outline-secondary"
              class="mr-1"
              :disabled="submittingReceive"
              @click="showReceiveModal = false"
            >
              {{ $t("common.cancel") }}
            </b-button>
            <b-button
              variant="success"
              :disabled="submittingReceive"
              @click="confirmReceivePayment"
            >
              <b-spinner
                v-if="submittingReceive"
                small
                class="mr-1"
              />
              {{ $t("cashiers.receive-payment") }}
            </b-button>
          </div>
        </div>
      </b-modal>

      <b-modal
        id="bulk-paid-receipt-modal"
        :visible.sync="showReceiptModal"
        size="xl"
        hide-footer
        :no-close-on-esc="!receiptPrinted"
        :no-close-on-backdrop="!receiptPrinted"
        :hide-header-close="!receiptPrinted"
        @show="onReceiptModalShow"
        @hide="onReceiptModalHide"
      >
        <template #modal-title>
          كشف حساب الكاشير اليومي
        </template>
        <bulk-paid-receipt
          :items="receiptItems"
          :refunds="receiptRefunds"
          :printed-by="printedByName"
          @print-started="onReceiptPrintStarted"
        />
      </b-modal>

      <b-modal
        id="bulk-confirm-modal"
        :visible.sync="showBulkConfirmModal"
        hide-footer
        @hide="showBulkConfirmModal = false"
      >
        <template #modal-title>
          تأكيد تسليم الكشف
        </template>
        <div class="my-2">
          <div
            class="mb-4 p-3 text-center"
            style="
              background-color: #fff3cd;
              border: 2px solid #ffc107;
              border-radius: 8px;
            "
          >
            <p
              class="mb-0 font-weight-bold"
              style="font-size: 1.3rem; color: #856404; line-height: 1.6"
            >
              تاكد من طباعة الكشف اليومي قبل تاكيد التسليم لهذا اليوم
            </p>
          </div>
          <p
            class="mb-3"
            style="line-height: 1.8"
          >
            هذا العملية سيتم من خلالها من خلالها نقل الفواتير الى المحاسب
            والمستلمة من قبلك تاكد من جميع الفواتير و المجموع مطابق للصندوق
          </p>
          <p
            class="mb-3 text-muted"
            style="font-size: 0.9rem"
          >
            الطباعة هنا مرة واحدة عند نهاية الشفت
          </p>
          <div class="d-flex justify-content-end">
            <b-button
              variant="outline-secondary"
              class="mr-1"
              :disabled="submittingBulk"
              @click="showBulkConfirmModal = false"
            >
              {{ $t("common.cancel") }}
            </b-button>
            <b-button
              variant="success"
              :disabled="submittingBulk"
              @click="confirmBulkMarkSelected"
            >
              <b-spinner
                v-if="submittingBulk"
                small
                class="mr-1"
              />
              تأكيد التسليم
            </b-button>
          </div>
        </div>
      </b-modal>
    </b-card>
  </div>
</template>

<script>
import {
  BRow,
  BCol,
  BCard,
  BTable,
  BButton,
  BFormGroup,
  BFormCheckbox,
  BFormSelect,
  BFormInput,
  BPagination,
  BSpinner,
  BModal,
} from 'bootstrap-vue'
import Datepicker from 'vuejs-datepicker'
import vSelect from 'vue-select'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

const INVOICE_TYPE_MAP = {
  invoice_out_clinic: 'cashiers.invoice-type-out-clinic',
  invoice_nicu: 'cashiers.invoice-type-nicu',
  invoice_patient_card_test: 'cashiers.invoice-type-patient-card-test',
  refund: 'cashiers.invoice-type-refund',
  invoice_insurance_surgery: 'cashiers.invoice-type-insurance-surgery',
  invoice_emergency: 'cashiers.invoice-type-emergency',
}

// Matches BulkPaidReceipt INVOICE_TYPE_LABELS for print section fallbacks
const RECEIPT_INVOICE_TYPE_LABELS = {
  invoice_out_clinic: 'عيادة خارجية',
  invoice_nicu: 'الخدج / العناية المركزة',
  invoice_patient_card_test: 'تحاليل / فحوصات',
  refund: 'استرجاع',
  invoice_insurance_surgery: 'عملية تأمين',
  invoice_emergency: 'طوارئ',
}
const AUTO_SEARCH_DELAY = 400

export default {
  name: 'PendingInvoices',
  components: {
    BRow,
    BCol,
    BCard,
    BTable,
    BButton,
    BFormGroup,
    BFormCheckbox,
    BFormSelect,
    BFormInput,
    BPagination,
    BSpinner,
    BModal,
    Datepicker,
    vSelect,
    BulkPaidReceipt: () => import('./components/BulkPaidReceipt.vue'),
  },
  data() {
    return {
      filterDate: null,
      filterType: null,
      filterServiceType: null,
      serviceTypeOptions: [],
      patientName: '',
      filterInvoiceNumber: '',
      loading: false,
      invoices: [],
      selectedIds: [],
      page: 1,
      take: 10,
      dataCount: 0,
      showReceiveModal: false,
      selectedInvoiceId: null,
      submittingReceive: false,
      showReceiptModal: false,
      receiptPrinted: false,
      showBulkConfirmModal: false,
      submittingBulk: false,
      receiptItems: [],
      receiptRefunds: [],
      printedByName: '',
      pageSizeOptions: [
        { value: 10, text: '10' },
        { value: 15, text: '15' },
        { value: 25, text: '25' },
        { value: 50, text: '50' },
        { value: 100, text: '100' },
      ],
      tableColumns: [
        { key: 'type', label: this.$t('common.type'), sortable: true },
        {
          key: 'number',
          label: this.$t('common.invoice-number'),
          sortable: true,
        },
        {
          key: 'patientName',
          label: this.$t('reception.patient-name'),
          sortable: false,
        },
        {
          key: 'itemTitle',
          label: this.$t('hospital.service'),
          sortable: false,
        },
        // {
        //   key: 'serviceTypeName',
        //   label: 'نوع الخدمة',
        //   sortable: false,
        // },
        { key: 'amount', label: this.$t('common.amount'), sortable: true },
        { key: 'discount', label: this.$t('common.discount'), sortable: true },
        {
          key: 'approvedBy',
          label: 'موافق بواسطة',
          sortable: false,
        },
        {
          key: 'approvedDate',
          label: 'تاريخ الموافقة',
          sortable: true,
        },
        {
          key: 'createdBy',
          label: 'المنشئ',
          sortable: false,
        },
        {
          key: 'createdAt',
          label: 'تاريخ الإنشاء',
          sortable: true,
        },
        // { key: "actions", label: this.$t("common.actions") },
      ],
      autoSearchTimeout: null,
    }
  },
  computed: {
    invoiceTypeOptions() {
      const options = [{ value: null, text: this.$t('common.all') }]
      Object.keys(INVOICE_TYPE_MAP).forEach(key => {
        options.push({
          value: key,
          text: this.formatInvoiceType(key),
        })
      })
      return options
    },
    serviceTypeFilterOptions() {
      const options = [{ value: null, text: this.$t('common.all') }]
      this.serviceTypeOptions.forEach(st => {
        options.push({
          value: st.id,
          text: st.name,
        })
      })
      return options
    },
    preparedInvoices() {
      if (!Array.isArray(this.invoices)) return []
      return this.invoices.map(raw => ({
        invoiceId: raw.id,
        type: raw.type,
        number: raw.invoice_no,
        patientName:
          (raw.patientCard
            && raw.patientCard.patient
            && raw.patientCard.patient.name)
          || (raw.patient_card
            && raw.patient_card.patient
            && raw.patient_card.patient.name)
          || '-',
        itemTitle:
          (raw.service && raw.service.title)
          || (raw.surgery && raw.surgery.title)
          || '-',
        serviceTypeName:
          (raw.service
            && raw.service.type
            && raw.service.type.name)
          || '-',
        amount: raw.amount,
        discount: raw.discount || 0,
        currency: raw.currency || 'IQD',
        status: raw.status,
        approvedBy: raw.approved_by || raw.approvedBy || null,
        approvedDate: raw.approved_at || null,
        createdBy:
          (raw.createdBy && raw.createdBy.name)
          || (raw.created_by && raw.created_by.name)
          || '-',
        createdAt: raw.created_at,
      }))
    },
    currentUserName() {
      try {
        const s = this.$store && this.$store.state
        if (!s) return ''
        if (s.auth && s.auth.user && (s.auth.user.name || s.auth.user.username)) return s.auth.user.name || s.auth.user.username
        if (s.user && (s.user.name || s.user.username)) return s.user.name || s.user.username
        return ''
      } catch (e) {
        return ''
      }
    },
    pageInvoiceIds() {
      return this.preparedInvoices.map(i => i.invoiceId)
    },
    allSelectedOnPage() {
      if (this.pageInvoiceIds.length === 0) return false
      return this.pageInvoiceIds.every(id => this.selectedIds.includes(id))
    },
    isIndeterminate() {
      const countOnPage = this.pageInvoiceIds.filter(id => this.selectedIds.includes(id)).length
      return countOnPage > 0 && countOnPage < this.pageInvoiceIds.length
    },
  },
  watch: {
    page() {
      this.fetchPendingInvoices()
    },
    filterDate() {
      this.handleFilterChange()
    },
    filterType() {
      this.handleFilterChange()
    },
    filterServiceType() {
      this.handleFilterChange()
    },
    patientName() {
      this.handleFilterChange()
    },
    filterInvoiceNumber() {
      this.handleFilterChange()
    },
  },
  mounted() {
    this.loadServiceTypes()
    this.fetchPendingInvoices()
  },
  beforeDestroy() {
    if (this.autoSearchTimeout) {
      clearTimeout(this.autoSearchTimeout)
    }
  },
  methods: {
    handleFilterChange() {
      if (this.page !== 1) {
        this.page = 1
        return
      }
      this.queueAutoSearch()
    },
    queueAutoSearch() {
      if (this.autoSearchTimeout) {
        clearTimeout(this.autoSearchTimeout)
      }
      this.autoSearchTimeout = setTimeout(() => {
        this.fetchPendingInvoices()
      }, AUTO_SEARCH_DELAY)
    },
    async loadServiceTypes() {
      try {
        await this.getList('serviceTypes?page=1&take=10&sort=-id')
        const rows = Array.isArray(this.all_data?.data)
          ? this.all_data.data
          : []
        this.serviceTypeOptions = rows.map(r => ({
          id: r.id,
          name: r.name,
        }))
      } catch (e) {
        this.serviceTypeOptions = []
      }
    },
    getReceiptSectionMeta(invoice) {
      const svc = invoice.service
      const typeObj = svc && svc.type
      if (typeObj && typeObj.id) {
        return {
          sectionGroupKey: `st:${typeObj.id}`,
          sectionTitle: typeObj.name || '—',
        }
      }
      const invType = invoice.type || 'other'
      return {
        sectionGroupKey: `inv:${invType}`,
        sectionTitle: RECEIPT_INVOICE_TYPE_LABELS[invType] || 'أخرى',
      }
    },
    onPageSizeChange() {
      this.page = 1 // Reset to first page when changing page size
      this.fetchPendingInvoices()
    },
    onReceiptModalShow() {
      this.receiptPrinted = false
    },
    onReceiptPrintStarted() {
      this.receiptPrinted = true
      this.showReceiptModal = false
    },
    onReceiptModalHide(bvEvt) {
      if (
        !this.receiptPrinted
        && bvEvt
        && typeof bvEvt.preventDefault === 'function'
      ) {
        // Block closing until print has started
        bvEvt.preventDefault()
        return
      }
      this.receiptItems = []
      this.receiptRefunds = []
    },
    async fetchPendingInvoices() {
      this.loading = true
      try {
        let url = `invoices/pending?page=${this.page}&take=${this.take}&sort=-id&include=patientCard,createdBy`
        if (this.filterDate) {
          const { startOfDay, nextDay } = this.getDateRangeParams(
            this.filterDate,
          )
          url += `&filter[created_at-ge]=${startOfDay}&filter[created_at-lt]=${nextDay}`
        }
        if (this.filterType) {
          url += `&filter[type]=${this.filterType}`
        }
        if (this.filterServiceType) {
          url += `&filter[service_type_id]=${this.filterServiceType}`
        }
        if (this.patientName && this.patientName.trim()) {
          const patientCardIds = await this.fetchPatientCardIdsByName(
            this.patientName.trim(),
          )
          if (patientCardIds.length === 0) {
            this.invoices = []
            this.dataCount = 0
            return
          }
          url += `&filter[patient_card_id]=${patientCardIds.join(',')}`
        }
        if (this.filterInvoiceNumber && this.filterInvoiceNumber.trim()) {
          url += `&filter[invoice_no]=${encodeURIComponent(
            this.filterInvoiceNumber.trim(),
          )}`
        }
        await this.getList(url)
        console.log(this.all_data, 'data from pending invoices')
        if (this.all_data) {
          // Handle Laravel pagination structure (current_page, data, per_page, total)
          if (this.all_data.data && Array.isArray(this.all_data.data)) {
            this.invoices = this.all_data.data
          } else if (Array.isArray(this.all_data)) {
            this.invoices = this.all_data
          }

          // Extract pagination info from Laravel pagination response
          if (this.all_data.current_page !== undefined) {
            this.page = this.all_data.current_page || 1
            this.take = this.all_data.per_page || this.take
            this.dataCount = this.all_data.total || 0
          } else if (this.all_data.meta) {
            // Handle meta pagination structure
            this.page = this.all_data.meta.page || 1
            this.take = this.all_data.meta.take
              || this.all_data.meta.pageSize
              || this.take
            this.dataCount = this.all_data.meta.total || 0
          } else if (this.all_data.total !== undefined) {
            // Fallback: just total
            this.dataCount = this.all_data.total
          }
        }
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: this.$t('common.error'),
            icon: 'AlertCircleIcon',
            variant: 'danger',
            text:
              error.response?.data?.message
              || this.$t('cashiers.failed-fetch-pending'),
          },
        })
      } finally {
        this.loading = false
      }
    },
    toggleSelectAll(checked) {
      if (checked) {
        // Add all ids on the current page
        const set = new Set(this.selectedIds)
        this.pageInvoiceIds.forEach(id => set.add(id))
        this.selectedIds = Array.from(set)
      } else {
        // Remove all ids on the current page
        const pageSet = new Set(this.pageInvoiceIds)
        this.selectedIds = this.selectedIds.filter(id => !pageSet.has(id))
      }
    },
    formatDate(dateString) {
      if (!dateString) return '-'
      const date = new Date(dateString)
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    },
    formatAmount(amount, currency = 'USD') {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency,
      }).format(amount)
    },
    formatDateForAPI(date) {
      if (!date) return null
      if (typeof date === 'string') return date
      const d = new Date(date)
      return d.toISOString().split('T')[0]
    },
    formatDateTimeForAPI(date) {
      const d = new Date(date)
      const year = d.getFullYear()
      const month = String(d.getMonth() + 1).padStart(2, '0')
      const day = String(d.getDate()).padStart(2, '0')
      const hours = String(d.getHours()).padStart(2, '0')
      const minutes = String(d.getMinutes()).padStart(2, '0')
      const seconds = String(d.getSeconds()).padStart(2, '0')
      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`
    },
    getDateRangeParams(date) {
      const base = new Date(date)
      const start = new Date(base)
      start.setHours(0, 0, 0, 0)
      const nextDay = new Date(start)
      nextDay.setDate(nextDay.getDate() + 1)
      return {
        startOfDay: this.formatDateTimeForAPI(start),
        nextDay: this.formatDateTimeForAPI(nextDay),
      }
    },
    getFallbackServiceNameByType(type) {
      switch (type) {
        case 'invoice_out_clinic':
          return 'زيارة عيادة خارجية'
        case 'invoice_insurance_surgery':
          return 'عملية جراحية (تأمين)'
        case 'invoice_patient_card_test':
          return 'تحاليل / فحوصات مخبرية'
        case 'invoice_nicu':
          return 'أقساط العناية المركزة للخدج'
        case 'invoice_emergency':
          return 'خدمات قسم الطوارئ'
        default:
          return 'خدمة بدون اسم'
      }
    },
    formatInvoiceType(type) {
      if (!type) return '-'
      // Format the key: snake_case to Title Case
      const formattedKey = type
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')

      const translationKey = INVOICE_TYPE_MAP[type]
      if (translationKey) {
        const translated = this.$t(translationKey)
        // If translation exists and is different from key, return both
        if (translated && translated !== translationKey) {
          return `${translated} (${formattedKey})`
        }
      }
      // Fallback: return formatted key only
      return formattedKey
    },
    openReceiveModal(invoiceId) {
      this.selectedInvoiceId = invoiceId
      this.showReceiveModal = true
    },
    async confirmReceivePayment() {
      if (!this.selectedInvoiceId) return
      this.submittingReceive = true
      try {
        await this.$store.dispatch('put', {
          path: `invoices/${this.selectedInvoiceId}/receive-payment`,
          form: {},
        })
        this.$toast({
          component: ToastificationContent,
          props: {
            title: this.$t('common.success'),
            icon: 'CheckIcon',
            variant: 'success',
            text: this.$t('cashiers.payment-received-success'),
          },
        })
        this.showReceiveModal = false
        this.selectedInvoiceId = null
        await this.fetchPendingInvoices()
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: this.$t('common.error'),
            icon: 'AlertCircleIcon',
            variant: 'danger',
            text:
              error.response?.data?.message
              || this.$t('cashiers.payment-received-failed'),
          },
        })
      } finally {
        this.submittingReceive = false
      }
    },
    async confirmBulkMarkSelected() {
      this.submittingBulk = true
      this.showBulkConfirmModal = false
      try {
        const res = await this.create({
          path: 'invoices/bulk-mark-as-paid?include=createdBy,created_by',
        })
        console.log(
          res?.data?.data?.updated_invoices,
          'response from bulk mark as paid',
        )
        // Use the response data from API
        const updatedInvoices = res?.data?.data?.updated_invoices || []

        if (updatedInvoices.length === 0) {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: this.$t('common.warning'),
              icon: 'AlertCircleIcon',
              variant: 'warning',
              text: 'No invoices were updated',
            },
          })
          this.submittingBulk = false
          return
        }

        this.loading = true

        // Fetch patient card data to get patient names
        const patientCardIds = [
          ...new Set(
            updatedInvoices.map(inv => inv.patient_card_id).filter(Boolean),
          ),
        ]
        const patientCardMap = new Map()

        if (patientCardIds.length > 0) {
          try {
            const patientCardsUrl = `/patientCard?filter[id]=${patientCardIds.join(
              ',',
            )}&include=patient&take=1000`
            await this.getList(patientCardsUrl)
            const patientCards = Array.isArray(this.all_data?.data)
              ? this.all_data.data
              : []
            patientCards.forEach(card => {
              if (card.patient && card.patient.name) {
                patientCardMap.set(card.id, card.patient.name)
              }
            })
          } catch (error) {
            console.error('Error fetching patient cards:', error)
          }
        }

        // Split updated invoices into normal invoices and refunds
        const refundInvoices = updatedInvoices.filter(
          invoice => invoice.type === 'refund',
        )
        const normalInvoices = updatedInvoices.filter(
          invoice => invoice.type !== 'refund',
        )

        // Map the normal invoices to receipt format
        this.receiptItems = normalInvoices.map(invoice => {
          const patientName = patientCardMap.get(invoice.patient_card_id) || '-'
          // Try to resolve the best possible service / surgery name
          const serviceName = invoice.service_name
            || invoice.surgery_name
            || (invoice.service && invoice.service.title)
            || (invoice.surgery && invoice.surgery.title)
            || this.getFallbackServiceNameByType(invoice.type)

          const section = this.getReceiptSectionMeta(invoice)
          return {
            number: invoice.invoice_no || '-',
            type: invoice.type || 'other',
            formattedType: this.formatInvoiceType(invoice.type),
            patientName,
            itemTitle: serviceName,
            amount: Number(invoice.amount) || 0,
            discount: Number(invoice.discount) || 0,
            currency: invoice.currency || 'IQD',
            approved_by: invoice.approved_by || invoice.approvedBy || null,
            approved_at: invoice.approved_at || null,
            createdBy:
              invoice.created_by?.name || invoice.createdBy?.name || '-',
            createdAt: invoice.created_at || '-',
            sectionGroupKey: section.sectionGroupKey,
            sectionTitle: section.sectionTitle,
          }
        })

        // Keep raw refund invoices so the print component can render them accurately
        this.receiptRefunds = refundInvoices

        this.$toast({
          component: ToastificationContent,
          props: {
            title: this.$t('common.success'),
            icon: 'CheckIcon',
            variant: 'success',
            text: res?.data?.message || this.$t('cashiers.bulk-paid-success'),
          },
        })

        await this.fetchPrintedUserName()
        this.showReceiptModal = true
        this.selectedIds = []
        this.page = 1 // Reset to first page after bulk action
        await this.fetchPendingInvoices()
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: this.$t('common.error'),
            icon: 'AlertCircleIcon',
            variant: 'danger',
            text:
              error.response?.data?.message
              || this.$t('cashiers.bulk-paid-failed'),
          },
        })
      } finally {
        this.loading = false
        this.submittingBulk = false
      }
    },
    async fetchPrintedUserName() {
      try {
        await this.getList('auth/user-profile')
        if (this.all_data && this.all_data.user && this.all_data.user.name) {
          this.printedByName = this.all_data.user.name
        }
      } catch (e) {
        this.printedByName = ''
      }
    },
    async printCurrentInvoices() {
      if (!this.invoices || this.invoices.length === 0) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: this.$t('common.warning'),
            icon: 'AlertCircleIcon',
            variant: 'warning',
            text: 'No invoices to print',
          },
        })
        return
      }

      try {
        // Fetch patient card data to get patient names
        const patientCardIds = [
          ...new Set(
            this.invoices
              .map(inv => inv.patient_card_id || inv.patientCard?.id)
              .filter(Boolean),
          ),
        ]
        const patientCardMap = new Map()

        if (patientCardIds.length > 0) {
          try {
            const patientCardsUrl = `/patientCard?filter[id]=${patientCardIds.join(
              ',',
            )}&include=patient&take=1000`
            await this.getList(patientCardsUrl)
            const patientCards = Array.isArray(this.all_data?.data)
              ? this.all_data.data
              : []
            patientCards.forEach(card => {
              if (card.patient && card.patient.name) {
                patientCardMap.set(card.id, card.patient.name)
              }
            })
          } catch (error) {
            console.error('Error fetching patient cards:', error)
          }
        }

        // Split invoices into normal invoices and refunds
        const refundInvoices = this.invoices.filter(
          invoice => invoice.type === 'refund',
        )
        const normalInvoices = this.invoices.filter(
          invoice => invoice.type !== 'refund',
        )

        // Map the normal invoices to receipt format
        this.receiptItems = normalInvoices.map(invoice => {
          const patientCardId = invoice.patient_card_id
            || invoice.patientCard?.id
            || invoice.patient_card?.id
          const patientName = patientCardMap.get(patientCardId)
            || (invoice.patientCard
              && invoice.patientCard.patient
              && invoice.patientCard.patient.name)
            || (invoice.patient_card
              && invoice.patient_card.patient
              && invoice.patient_card.patient.name)
            || '-'

          // Try to resolve the best possible service / surgery name
          const serviceName = invoice.service_name
            || invoice.surgery_name
            || (invoice.service && invoice.service.title)
            || (invoice.surgery && invoice.surgery.title)
            || this.getFallbackServiceNameByType(invoice.type)

          const section = this.getReceiptSectionMeta(invoice)
          return {
            number: invoice.invoice_no || '-',
            type: invoice.type || 'other',
            formattedType: this.formatInvoiceType(invoice.type),
            patientName,
            itemTitle: serviceName,
            amount: Number(invoice.amount) || 0,
            discount: Number(invoice.discount) || 0,
            currency: invoice.currency || 'IQD',
            approved_by: invoice.approved_by || invoice.approvedBy || null,
            approved_at: invoice.approved_at || null,
            createdBy:
              invoice.created_by?.name
              || invoice.createdBy?.name
              || (invoice.created_by && typeof invoice.created_by === 'object'
                ? invoice.created_by.name
                : invoice.created_by)
              || '-',
            createdAt: invoice.created_at || '-',
            sectionGroupKey: section.sectionGroupKey,
            sectionTitle: section.sectionTitle,
          }
        })

        // Keep raw refund invoices so the print component can render them accurately
        this.receiptRefunds = refundInvoices

        await this.fetchPrintedUserName()
        this.showReceiptModal = true
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: this.$t('common.error'),
            icon: 'AlertCircleIcon',
            variant: 'danger',
            text:
              error.response?.data?.message
              || 'Failed to prepare invoices for printing',
          },
        })
      }
    },
    async fetchPatientCardIdsByName(name) {
      try {
        const encoded = encodeURIComponent(name)
        const patientCardsUrl = `/patientCard?filter[patient.name]=${encoded}&take=1000`
        await this.getList(patientCardsUrl)
        const patientCards = Array.isArray(this.all_data?.data)
          ? this.all_data.data
          : []
        return patientCards.map(card => card.id).filter(Boolean)
      } catch (error) {
        // If patient lookup fails, ignore the patient filter
        return []
      }
    },
  },
}
</script>

<style scoped>
.text-nowrap {
  white-space: nowrap;
}
</style>
